import { useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useOnboarding } from "@/hooks/use-onboarding";
import { PlanTier } from "@/hooks/use-subscription";
import { WelcomeStep } from "./WelcomeStep";
import { PlanSelectionStep } from "./PlanSelectionStep";
import { TermsStep } from "./TermsStep";
import { AccountStep } from "./AccountStep";
import { CompleteStep } from "./CompleteStep";
import { Check } from "lucide-react";

const STEPS = [
  { key: "welcome", label: "Boas-vindas", shortLabel: "Olá" },
  { key: "plan", label: "Plano", shortLabel: "Plano" },
  { key: "terms", label: "Termos", shortLabel: "Termos" },
  { key: "account", label: "Conta", shortLabel: "Conta" },
  { key: "complete", label: "Concluído", shortLabel: "✓" },
] as const;

export function OnboardingWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<PlanTier | null>(null);
  const { completeStep } = useOnboarding();

  const goToNext = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handlePlanSelected = useCallback(
    async (tier: PlanTier) => {
      setSelectedPlan(tier);
      try {
        await completeStep("has_selected_plan");
      } catch {
        // Non-blocking - user can still proceed
      }
    },
    [completeStep]
  );

  const renderStep = () => {
    switch (STEPS[currentStep].key) {
      case "welcome":
        return <WelcomeStep onNext={goToNext} />;
      case "plan":
        return (
          <PlanSelectionStep
            onNext={goToNext}
            onBack={goToPrevious}
            onPlanSelected={handlePlanSelected}
          />
        );
      case "terms":
        return <TermsStep onNext={goToNext} onBack={goToPrevious} />;
      case "account":
        return <AccountStep onNext={goToNext} onBack={goToPrevious} />;
      case "complete":
        return <CompleteStep />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-2 sm:p-4">
      <Card className="w-full max-w-3xl shadow-2xl">
        <CardContent className="pt-8 pb-6 px-4 sm:px-6 md:px-8">
          {/* Stepper */}
          <nav aria-label="Progresso do onboarding" className="mb-8">
            <ol className="flex items-center justify-between">
              {STEPS.map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;

                return (
                  <li
                    key={step.key}
                    className="flex flex-col items-center relative flex-1"
                  >
                    {/* Connector line */}
                    {index > 0 && (
                      <div
                        className={`absolute top-4 sm:top-5 -left-1/2 w-full h-0.5 -translate-y-1/2 ${
                          isCompleted
                            ? "bg-primary"
                            : "bg-muted"
                        }`}
                        aria-hidden="true"
                      />
                    )}

                    {/* Step circle */}
                    <div
                      className={`relative z-10 flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded-full border-2 text-xs sm:text-sm font-semibold transition-colors ${
                        isCompleted
                          ? "bg-primary border-primary text-primary-foreground"
                          : isCurrent
                          ? "border-primary bg-background text-primary"
                          : "border-muted bg-background text-muted-foreground"
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        index + 1
                      )}
                    </div>

                    {/* Step label */}
                    <span
                      className={`mt-1.5 sm:mt-2 font-medium text-[10px] sm:text-xs ${
                        isCurrent
                          ? "text-primary"
                          : isCompleted
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      <span className="sm:hidden">{step.shortLabel}</span>
                      <span className="hidden sm:inline">{step.label}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </nav>

          {/* Step content */}
          {renderStep()}
        </CardContent>
      </Card>
    </div>
  );
}
